import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@material-ui/core';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import styled from 'styled-components';
import { first } from 'lodash/fp';
import { getFullName } from '../../../utils';
import { useUser } from '../../../utils/hooks/auth';
import { useDateTimeService } from '../../../utils/hooks/dateTimeService';
import { usePriceService } from '../../../utils/hooks/priceService';
import { MicroAvatar, Typography } from '../../../ui-kit';
import { Employee } from './types';

const Circle = styled(Box)`
    background-color: #DCF7E5;
    border-radius: 50%;
    ${({ theme }) => `
        color: ${theme.palette.additional.green};
        width: ${theme.spacing(3)}px;
        height: ${theme.spacing(3)}px;
    `}
`;

interface CashFlowLeftColumnProps {
    startedBy: Employee;
    startedUtc: Date;
    initialCashAmount: number;
}

const CashFlowLeftColumn = ({ startedUtc, startedBy: { account }, initialCashAmount }: CashFlowLeftColumnProps) => {
    const { user: { cultureCode } } = useUser();
    const { dateTimeService } = useDateTimeService();
    const { priceService } = usePriceService();
    const { t } = useTranslation();

    const openedDateTime = dateTimeService.localizeDateTime(startedUtc, cultureCode);
    const fullName = getFullName(account);
    const initial = first(account.firstName);

    return (
        <Box p={3}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Typography uppercase color='textSecondary' variant='caption'>{t('Opened at')}</Typography>
                    <Typography>{openedDateTime}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography uppercase color='textSecondary' variant='caption'>{t('Opened by')}</Typography>
                    <Box display='flex'>
                        <Box mr={1}>
                            <MicroAvatar src={account.imageUrl || undefined}>{initial}</MicroAvatar>
                        </Box>
                        <Typography>{fullName}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography uppercase color='textSecondary' variant='caption'>{t('Starting cash')}</Typography>
                    <Box display='flex'>
                        <Circle mr={1}>
                            <AttachMoneyIcon />
                        </Circle>
                        <Typography>{priceService.toLocaleString(initialCashAmount)}</Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CashFlowLeftColumn;
